import { AdminHistory, AdminHistoryDTO } from "Types/Admin/AdminHistoryType";
import { ResultsDTO } from "Types/ResultsDTO";
import { ApiService } from "./ApiService";

export const HistoryApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getAllHistory: builder.query<AdminHistory[], { plant: string; search?: string }>({
      query: ({ plant, search }) => ({
        url: "/ecom-history/getAll",
        method: "GET",
        params: { plant, search: search ?? "" },
      }),
      transformResponse: (response: ResultsDTO<any[]>): AdminHistory[] => {
        const items: any[] = response?.results ?? [];
        return items.map((item, index): AdminHistory => {
          const hdr = item.ecomHistoryHdr ?? {};
          const id = item.id ?? hdr.id ?? index;
          const plant = item.plant ?? hdr.plant ?? "";
          return {
            id,
            plant,
            year: item.year ?? hdr.year ?? 0,
            title: item.title ?? hdr.title ?? "",
            description1: item.description1 ?? hdr.description1 ?? "",
            description2: item.description2 ?? hdr.description2 ?? "",
            description3: item.description3 ?? hdr.description3 ?? "",
            imageUrl: item.imageUrl ?? hdr.imageUrl ?? null,
            createdAt: item.crAt ?? hdr.crAt ?? null,
            updatedAt: item.modAt ?? hdr.modAt ?? null,
          };
        });
      },
      providesTags: ["History"],
    }),

    getHistoryById: builder.query<AdminHistory, { id: number; plant: string }>({
      query: ({ id, plant }) => ({
        url: "/ecom-history",
        method: "GET",
        params: { id, plant },
      }),
      transformResponse: (response: ResultsDTO<any>): AdminHistory => {
        const item = response?.results ?? {};
        const hdr = item.ecomHistoryHdr ?? {};
        const id = item.id ?? hdr.id ?? 0;
        const plant = item.plant ?? hdr.plant ?? "";
        return {
          id,
          plant,
          year: item.year ?? hdr.year ?? 0,
          title: item.title ?? hdr.title ?? "",
          description1: item.description1 ?? hdr.description1 ?? "",
          description2: item.description2 ?? hdr.description2 ?? "",
          description3: item.description3 ?? hdr.description3 ?? "",
          imageUrl: item.imageUrl ?? hdr.imageUrl ?? null,
          createdAt: item.crAt ?? hdr.crAt ?? null,
          updatedAt: item.modAt ?? hdr.modAt ?? null,
        };
      },
      providesTags: (result, error, arg) => [{ type: "History" as const, id: arg.id }],
    }),

    createHistory: builder.mutation<ResultsDTO, { data: AdminHistoryDTO; file?: File | null }>({
      query: ({ data, file }) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (file) {
          formData.append("file", file);
        } else {
          formData.append("file", new Blob());
        }
        return { url: "/ecom-history/save", method: "POST", body: formData };
      },
      invalidatesTags: ["History"],
    }),

    updateHistory: builder.mutation<ResultsDTO, { data: AdminHistoryDTO; file?: File | null }>({
      query: ({ data, file }) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (file) {
          formData.append("file", file);
        } else {
          formData.append("file", new Blob());
        }
        return { url: "/ecom-history/update", method: "PUT", body: formData };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "History" as const, id: arg.data.id },
        "History",
      ],
    }),

    deleteHistory: builder.mutation<ResultsDTO, { id: number; plant: string }>({
      query: ({ id, plant }) => ({
        url: "/ecom-history/delete",
        method: "DELETE",
        params: { id, plant },
      }),
      invalidatesTags: ["History"],
    }),

    deleteHistoryImage: builder.mutation<ResultsDTO, { id: number; plant: string }>({
      query: ({ id, plant }) => ({
        url: "/ecom-history/delete-image",
        method: "DELETE",
        params: { id, plant },
      }),
      invalidatesTags: ["History"],
    }),

    bulkDeleteHistory: builder.mutation<ResultsDTO, { ids: number[]; plant: string }>({
      query: ({ ids, plant }) => ({
        url: "/ecom-history/bulk-delete",
        method: "DELETE",
        params: { ids, plant },
      }),
      invalidatesTags: ["History"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllHistoryQuery,
  useGetHistoryByIdQuery,
  useLazyGetAllHistoryQuery,
  useLazyGetHistoryByIdQuery,
  useCreateHistoryMutation,
  useUpdateHistoryMutation,
  useDeleteHistoryMutation,
  useDeleteHistoryImageMutation,
  useBulkDeleteHistoryMutation,
} = HistoryApiSlice;
